import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { ResponsiveShell } from './components/ResponsiveShell';
import { PullToRefresh } from './components/PullToRefresh';
import { AuthScreen } from './screens/Auth';
import { Landing } from './screens/Landing';
import { AuthProvider, useSession } from './lib/auth';

const Home = lazy(() => import('./screens/Home').then((m) => ({ default: m.Home })));
const Profile = lazy(() => import('./screens/Profile').then((m) => ({ default: m.Profile })));
const Browse = lazy(() => import('./screens/Browse').then((m) => ({ default: m.Browse })));
const PatchDetail = lazy(() =>
  import('./screens/PatchDetail').then((m) => ({ default: m.PatchDetail }))
);
const CreateFlow = lazy(() =>
  import('./screens/CreateFlow').then((m) => ({ default: m.CreateFlow }))
);
const Inbox = lazy(() => import('./screens/Inbox').then((m) => ({ default: m.Inbox })));
const Sash = lazy(() => import('./screens/Sash').then((m) => ({ default: m.Sash })));
const Family = lazy(() => import('./screens/Family').then((m) => ({ default: m.Family })));
const KidProfile = lazy(() => import('./screens/KidProfile').then((m) => ({ default: m.KidProfile })));
const EditPatch = lazy(() => import('./screens/EditPatch').then((m) => ({ default: m.EditPatch })));
const Privacy = lazy(() => import('./screens/Privacy').then((m) => ({ default: m.Privacy })));

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <PullToRefresh />
          <RouteErrorBoundary>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              {/* Public routes — no app shell */}
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<AuthScreen />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* In-app routes — wrapped in ResponsiveShell, gated by Guard */}
              <Route
                path="/profile"
                element={
                  <AppRoute>
                    <Profile />
                  </AppRoute>
                }
              />
              <Route
                path="/patches"
                element={
                  <AppRoute>
                    <Browse />
                  </AppRoute>
                }
              />
              <Route
                path="/patches/new"
                element={
                  <AppRoute>
                    <CreateFlow />
                  </AppRoute>
                }
              />
              <Route
                path="/patches/:slug"
                element={
                  <AppRoute>
                    <PatchDetail />
                  </AppRoute>
                }
              />
              <Route
                path="/patches/:slug/edit"
                element={
                  <AppRoute>
                    <EditPatch />
                  </AppRoute>
                }
              />
              <Route
                path="/inbox"
                element={
                  <AppRoute>
                    <Inbox />
                  </AppRoute>
                }
              />
              <Route
                path="/sash"
                element={
                  <AppRoute>
                    <Sash />
                  </AppRoute>
                }
              />
              <Route
                path="/family"
                element={
                  <AppRoute>
                    <Family />
                  </AppRoute>
                }
              />
              <Route
                path="/family/:kidId"
                element={
                  <AppRoute>
                    <KidProfile />
                  </AppRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Smart root: signed-in users see the app's Home (Camp); everyone else sees the marketing Landing.
function RootRoute() {
  const { status } = useSession();
  if (status === 'loading') return <RouteLoading />;
  if (status === 'ready') {
    return (
      <AppRoute>
        <Home />
      </AppRoute>
    );
  }
  return <Landing />;
}

function AppRoute({ children }: { children: ReactNode }) {
  return (
    <ResponsiveShell>
      <Guard>{children}</Guard>
    </ResponsiveShell>
  );
}

function Guard({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteLoading />;
  if (status !== 'ready') return <Navigate to="/login" replace state={{ from: location }} />;

  return <>{children}</>;
}

function RouteLoading() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--paper)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'JetBrains Mono',
        fontSize: 11,
        color: 'var(--ink)',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        zIndex: 0,
      }}
    >
      Loading…
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Catches lazy-chunk failures (stale service worker after a deploy) and
// any render-time errors. Without this the route tree just blanks to the
// dark body color when an import rejects, with no feedback for the user.
// ─────────────────────────────────────────────────────────────────────
class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RouteErrorBoundary]', error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  hardReload = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // best-effort
    }
    window.location.reload();
  };

  render() {
    const err = this.state.error;
    if (!err) return this.props.children;

    const msg = err.message ?? '';
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('error loading dynamically imported') ||
      err.name === 'ChunkLoadError';

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--paper)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          gap: 14,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontFamily: 'JetBrains Mono',
            fontSize: 11,
            letterSpacing: '0.18em',
            color: 'var(--brick)',
          }}
        >
          ★ SOMETHING WENT WRONG ★
        </div>
        <div
          style={{
            fontFamily: 'Bevan',
            fontSize: 24,
            color: 'var(--ink)',
            letterSpacing: '0.04em',
            maxWidth: 380,
          }}
        >
          {isChunkError ? "COULDN'T LOAD THIS PAGE" : 'THIS SCREEN HIT A SNAG'}
        </div>
        <div
          style={{
            fontFamily: 'Newsreader, serif',
            fontSize: 15,
            color: 'var(--ink-soft)',
            maxWidth: 380,
            lineHeight: 1.5,
          }}
        >
          {isChunkError
            ? 'A new version of the app shipped. Reloading should pick it up.'
            : 'Reloading usually clears it.'}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={this.hardReload}
            style={{
              fontFamily: 'Bevan',
              fontSize: 13,
              letterSpacing: '0.08em',
              padding: '12px 22px',
              border: '1.5px solid var(--ink)',
              background: 'var(--brick)',
              color: 'var(--cream)',
              cursor: 'pointer',
              boxShadow: '3px 3px 0 var(--ink)',
              textTransform: 'uppercase',
            }}
          >
            Reload
          </button>
          {!isChunkError && (
            <button
              type="button"
              onClick={this.reset}
              style={{
                fontFamily: 'JetBrains Mono',
                fontSize: 11,
                letterSpacing: '0.14em',
                padding: '12px 18px',
                border: '1.5px solid var(--ink)',
                background: 'transparent',
                color: 'var(--ink)',
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              Try again
            </button>
          )}
        </div>
        {import.meta.env.DEV && (
          <pre
            style={{
              marginTop: 20,
              maxWidth: 600,
              overflow: 'auto',
              fontFamily: 'JetBrains Mono',
              fontSize: 10,
              color: 'var(--ink-soft)',
              background: 'var(--cream)',
              border: '1.5px solid var(--ink-soft)',
              padding: 10,
              textAlign: 'left',
            }}
          >
            {err.stack ?? msg}
          </pre>
        )}
      </div>
    );
  }
}
